import type { Answer } from './types.ts';

export type AnswerMap = Record<string, Answer>;

const key = (funnelCode: string) => `bf:${funnelCode}:answers`;

export function loadAnswers(funnelCode: string): AnswerMap {
  try {
    const raw = sessionStorage.getItem(key(funnelCode));
    return raw ? (JSON.parse(raw) as AnswerMap) : {};
  } catch {
    return {};
  }
}

export function saveAnswers(funnelCode: string, answers: AnswerMap): void {
  try {
    sessionStorage.setItem(key(funnelCode), JSON.stringify(answers));
  } catch {
    /* private mode — answers stay in memory only */
  }
}

export function clearAnswers(funnelCode: string): void {
  try {
    sessionStorage.removeItem(key(funnelCode));
  } catch { /* ignore */ }
}

/**
 * Template root for `answers.*`: every stored answer plus the derived counts
 * (`answers.count`, `answers.<key>.count`).
 */
export function answersRoot(answers: AnswerMap): Record<string, unknown> {
  const root: Record<string, unknown> = { ...answers };
  root.count = Object.keys(answers).length;
  for (const [k, v] of Object.entries(answers)) {
    if (v && typeof v === 'object' && !('count' in v)) {
      const listy = v as { values?: unknown[]; yes?: unknown[]; order?: unknown[] };
      const list = listy.values ?? listy.yes ?? listy.order;
      if (Array.isArray(list)) root[k] = { ...v, count: list.length };
    }
  }
  return root;
}

/** Numeric value of an answer, for rules. */
export function numeric(a: Answer | undefined): number | undefined {
  if (!a || typeof a !== 'object') return undefined;
  const v = (a as { value?: unknown }).value;
  return typeof v === 'number' ? v : undefined;
}

/** String value of an answer, for rules. */
export function stringly(a: Answer | undefined): string | undefined {
  if (!a || typeof a !== 'object') return undefined;
  const v = (a as { value?: unknown }).value;
  return typeof v === 'string' ? v : undefined;
}
